import React from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line

import LogoImage from "images/logo.png";
import { ReactComponent as FacebookIcon } from "images/facebook-icon.svg";
import { ReactComponent as TwitterIcon } from "images/twitter-icon.svg";
import { ReactComponent as YoutubeIcon } from "images/youtube-icon.svg";
import { ReactComponent as SvgDecoratorBlob1 } from "images/svg-decorator-blob-9.svg";
import config from "lib/config";

const Container = tw.div`relative bg-secondary-900 text-gray-100 -mb-8 -mx-8 px-8 py-20 lg:py-24`;
const Content = tw.div`max-w-screen-xl mx-auto relative z-10`;
const FiveColumns = tw.div`flex flex-wrap text-center sm:text-left justify-center sm:justify-start md:justify-between -mt-12`;

const Column = tw.div`px-4 sm:px-0 sm:w-1/3 md:w-auto mt-12`;

const ColumnHeading = tw.h5`uppercase font-bold`;

const LinkList = tw.ul`mt-6 text-sm font-medium`;
const LinkListItem = tw.li`mt-3`;
const Link = tw.a`border-b-2 border-transparent hocus:border-primary-500 hocus:text-primary-500 pb-1 transition duration-300`;

const Divider = tw.div`my-16 border-b border-gray-800 w-full`;

const ThreeColRow = tw.div`flex flex-col md:flex-row items-center justify-between`;

const LogoContainer = tw.div`flex items-center justify-center md:justify-start`;
const LogoImg = tw.img`w-10`;
const LogoTextBlock = tw.div`ml-2 flex flex-col leading-tight`;
const LogoText = tw.h5`text-xl font-black tracking-wider text-gray-100 m-0`;
const LogoSubText = styled.p`
  ${tw`text-sm font-medium text-primary-500 m-0`}
  letter-spacing: 1.5em;
`;
const CopywrightNotice = tw.p`text-center text-sm sm:text-base mt-8 md:mt-0 font-medium text-gray-400`;

const SocialLinksContainer = tw.div`mt-8 md:mt-0 flex`;
const SocialLink = styled.a`
  ${tw`cursor-pointer p-2 rounded-full bg-gray-100 text-gray-900 hover:bg-gray-400 transition duration-300 mr-4 last:mr-0`}
  svg {
    ${tw`w-4 h-4`}
  }
`;

const DecoratorBlobContainer = tw.div`absolute inset-0 overflow-hidden rounded-lg`;
const DecoratorBlob1 = tw(
  SvgDecoratorBlob1,
)`absolute top-0 left-0 w-80 h-80 transform -translate-x-20 -translate-y-32 text-primary-700 opacity-15`;
const DecoratorBlob2 = tw(
  SvgDecoratorBlob1,
)`absolute bottom-0 right-0 w-80 h-80 transform  translate-x-32 translate-y-48 text-primary-700 opacity-25`;

const LandingFooter = () => {
  return (
    <Container>
      <Content>
        <FiveColumns>
          <Column>
            <ColumnHeading>Navigation</ColumnHeading>
            <LinkList>
              <LinkListItem>
                <Link href="/">Accueil</Link>
              </LinkListItem>
              <LinkListItem>
                <Link href="/about">À propos</Link>
              </LinkListItem>
              <LinkListItem>
                <Link href="/services">Nos services</Link>
              </LinkListItem>
              <LinkListItem>
                <Link href="/contact">Contact</Link>
              </LinkListItem>
            </LinkList>
          </Column>
          <Column>
            <ColumnHeading>Services</ColumnHeading>
            <LinkList>
              <LinkListItem>
                <Link href="/services/avi">Attestation AVI</Link>
              </LinkListItem>
              <LinkListItem>
                <Link href="/services/visa">Accompagnement Visa</Link>
              </LinkListItem>
              <LinkListItem>
                <Link href="/services/logement">Logement Étudiant</Link>
              </LinkListItem>
              <LinkListItem>
                <Link href="/services/billet">Billets d'avion</Link>
              </LinkListItem>
            </LinkList>
          </Column>
          <Column>
            <ColumnHeading>Destinations</ColumnHeading>
            <LinkList>
              <LinkListItem>
                <Link href="/destinations/france">France</Link>
              </LinkListItem>
              <LinkListItem>
                <Link href="/destinations/belgique">Belgique</Link>
              </LinkListItem>
              <LinkListItem>
                <Link href="/destinations/canada">Canada</Link>
              </LinkListItem>
              <LinkListItem>
                <Link href="/destinations/luxembourg">Luxembourg</Link>
              </LinkListItem>
            </LinkList>
          </Column>
          <Column>
            <ColumnHeading>Compte</ColumnHeading>
            <LinkList>
              <LinkListItem>
                <Link href="/login">Connexion</Link>
              </LinkListItem>
              <LinkListItem>
                <Link href="/contact">Inscription</Link>
              </LinkListItem>
              <LinkListItem>
                <Link href="/dashboard">Mon espace</Link>
              </LinkListItem>
              <LinkListItem>
                <Link href="/contact">Nous contacter</Link>
              </LinkListItem>
            </LinkList>
          </Column>
          <Column>
            <ColumnHeading>Légal</ColumnHeading>
            <LinkList>
              <LinkListItem>
                <Link href="/privacy">Confidentialité</Link>
              </LinkListItem>
              <LinkListItem>
                <Link href="/terms">CGU</Link>
              </LinkListItem>
              <LinkListItem>
                <Link href="/mentions-legales">Mentions légales</Link>
              </LinkListItem>
              <LinkListItem>
                <Link href="/contact">Support</Link>
              </LinkListItem>
            </LinkList>
          </Column>
        </FiveColumns>
        <Divider />
        <ThreeColRow>
          <LogoContainer>
            <LogoImg src={LogoImage} />
            <LogoTextBlock>
              <LogoText>{config.appName}</LogoText>
              <LogoSubText>{config.subAppName}</LogoSubText>
            </LogoTextBlock>
          </LogoContainer>
          <CopywrightNotice>
            &copy; {new Date().getFullYear()} {config.appName}. Tous droits
            réservés.
          </CopywrightNotice>
          <SocialLinksContainer>
            <SocialLink
              href="https://facebook.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FacebookIcon />
            </SocialLink>
            <SocialLink
              href="https://twitter.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              <TwitterIcon />
            </SocialLink>
            <SocialLink
              href="https://youtube.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              <YoutubeIcon />
            </SocialLink>
          </SocialLinksContainer>
        </ThreeColRow>
      </Content>
      <DecoratorBlobContainer>
        <DecoratorBlob1 />
        <DecoratorBlob2 />
      </DecoratorBlobContainer>
    </Container>
  );
};

export default LandingFooter;
